import { LayoutGraph, NodeWithLevel } from './types';

/**
 * Assigns levels to nodes starting from roots at level 0.
 *
 * Root nodes (terminal nodes with no outputs) are at level 0. Each node's level is determined by
 * the maximum level of all nodes that it sends to, plus 1.
 *
 * Uses a reverse topological sort approach, walking backwards from roots.
 */
export function assignLevels(graph: LayoutGraph, rootIds: number[], nodeIdsInSet: Set<number>): Map<number, number> {
    const levels = new Map<number, number>();
    const outDegree = new Map<number, number>();
    const reverseAdjacencyList = new Map<number, number[]>();

    // Initialize out-degree and reverse adjacency list for nodes in this set
    for (const nodeId of nodeIdsInSet) {
        outDegree.set(nodeId, 0);
        reverseAdjacencyList.set(nodeId, []);
    }

    // Build reverse adjacency list (receiving -> sending) and count out-degrees
    for (const conn of graph.connections) {
        if (nodeIdsInSet.has(conn.sendingNodeId) && nodeIdsInSet.has(conn.receivingNodeId)) {
            reverseAdjacencyList.get(conn.receivingNodeId)!.push(conn.sendingNodeId);
            outDegree.set(conn.sendingNodeId, outDegree.get(conn.sendingNodeId)! + 1);
        }
    }

    // Initialize roots at level 0
    const queue: number[] = [...rootIds];
    for (const rootId of rootIds) {
        levels.set(rootId, 0);
    }

    // Process nodes in reverse topological order
    while (queue.length > 0) {
        const currentNode = queue.shift()!;
        const currentLevel = levels.get(currentNode)!;

        // Process all predecessors (nodes that send to this one)
        const predecessors = reverseAdjacencyList.get(currentNode) || [];
        for (const predecessor of predecessors) {
            // Update predecessor's level to be max of its current level and currentLevel + 1
            const newLevel = currentLevel + 1;
            const existingLevel = levels.get(predecessor);

            if (existingLevel === undefined || newLevel > existingLevel) {
                levels.set(predecessor, newLevel);
            }

            // Decrease out-degree
            const currentOutDegree = outDegree.get(predecessor)!;
            outDegree.set(predecessor, currentOutDegree - 1);

            // If all outgoing edges processed, add to queue
            if (outDegree.get(predecessor) === 0) {
                queue.push(predecessor);
            }
        }
    }

    return levels;
}
